import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadAcceptanceToTakePartInServiceTypes,
    loadDisabilityDetails,
    loadHasDisabilityDetails,
    loadMentalHealthDetails,
    loadModStatusDetails
} from '../../../../store/directusService';
import {
    clearCurrentParticipantCircumstances,
    clearParticipantAuditEventSearchData,
    loadParticipantAuditEvent,
    loadParticipantCircumstances,
    updateParticipantCircumstances
} from '../../../../store/participantService';
import { getConfiguredItems, getObjFromId } from '../../../../utils/directusFunctions';
import { EDIT_ON_CLOSED_ROLES } from '../../../../utils/formValidation/commonConstants';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../../utils/formValidation/commonErrorConstants';
import { checkDiff } from '../../../../utils/formValidation/errorCheckingFunctions';
import {
    ACCEPTANCE_TO_TAKE_PART_IN_SERVICE_ERROR,
    DISABILITY_DETAILS_ERROR,
    HAS_DISABILITY_DETAILS_ERROR,
    MENTAL_HEALTH_DETAILS_ERROR,
    MOD_STATUS_DETAILS_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../../utils/objectUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import AuditCircumstancesModal from '../../../auditing/auditCircumstances/AuditCircumstancesModal';
import Button from '../../../formElements/Button';
import MultiSelect from '../../../formElements/MultiSelect';
import RadioButtons from '../../../formElements/RadioButtons';
import SingleSelect from '../../../formElements/SingleSelect';
import Switch from '../../../formElements/Switch';
import TextAreaField from '../../../formElements/TextAreaField';
import TextInputField from '../../../formElements/TextInputField';
import LoadingSpinner from '../../../ui/LoadingSpinner';
import AdditionalContacts from '../../additionalContacts/AdditionalContacts';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const _HEALTH_COMMENT_MAX_LENGTH = 100;
const _REASONABLE_ADJUSTMENTS_MAX_LENGTH = 250;
const _TOOL_TIP_MESSAGE =
    "Do not record any details of the participant's status in relation to this on the record. If further information is required, consult the relevant Business Manager for the participant";
const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const Circumstances = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        participantId: '',
        hasDisabilityId: '',
        hasDisability: '',
        disabilityDetailIds: [],
        hasMentalHealthProblems: null,
        mentalHealthConditionIds: [],
        reasonableAdjustments: '',
        acceptanceOfServiceId: '',
        healthComment: '',
        hasCriminalRecord: null,
        criminalConvictionSpent: null,
        modStatusId: '',
        additionalContactCards: [],
        pv: false,
        trainingEmploymentRestriction: false,
        ucb: false,
        receiveServiceInEnglish: true
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');
    const [errors, setErrors] = useState({});
    const [keys, setKeys] = useState({
        modStatus: '0',
        hasDisabilityId: '1',
        disabilityDetailIds: '2',
        mentalHealthConditionIds: '3',
        acceptanceOfServiceId: '4'
    });
    const [arrayDisabilities, setArrayDisabilities] = useState([]);
    const [arrayMentalHealth, setArrayMentalHealth] = useState([]);
    const [arrayModStatus, setArrayModStatus] = useState([]);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const acceptanceToTakePartInServiceTypes = useSelector(
        (state) => state.entities.directusService.acceptanceToTakePartInServiceTypes
    );
    const mentalHealthDetails = useSelector(
        (state) => state.entities.directusService.mentalHealthDetails
    );
    const disabilityDetails = useSelector(
        (state) => state.entities.directusService.disabilityDetails
    );
    const hasDisabilityDetails = useSelector(
        (state) => state.entities.directusService.hasDisabilityDetails
    );
    const modStatusDetails = useSelector(
        (state) => state.entities.directusService.modStatusDetails
    );
    const participantCircumstances = useSelector(
        (state) => state.entities.participantService.currentParticipantCircumstances
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => () => cleanUp(), []);

    useEffect(() => {
        acceptanceToTakePartInServiceTypes?.length < 1 &&
            dispatch(loadAcceptanceToTakePartInServiceTypes());
        disabilityDetails?.length < 1 && dispatch(loadDisabilityDetails());
        hasDisabilityDetails?.length < 1 && dispatch(loadHasDisabilityDetails());
        mentalHealthDetails?.length < 1 && dispatch(loadMentalHealthDetails());
        modStatusDetails?.length < 1 && dispatch(loadModStatusDetails());
        dispatch(loadParticipantCircumstances(currentParticipant?.id));

        if (!currentParticipant?.id) return;
        const isDisabledOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || isDisabledOnClosedStatus);
    }, [currentParticipant.id]);

    useEffect(() => {
        if (Object.keys(participantCircumstances)?.length > 0) {
            setNewEntry(participantCircumstances);
            setInitialStateOnEntry(participantCircumstances);
        } else setInitialStateOnEntry({});
    }, [participantCircumstances]);

    useEffect(() => {
        if (disabilityDetails?.length > 0 && currentParticipant?.contractId) {
            const configItems = getConfiguredItems(
                disabilityDetails,
                currentParticipant?.contractId
            );
            setArrayDisabilities(configItems);
        }
    }, [disabilityDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (mentalHealthDetails?.length > 0 && currentParticipant?.contractId) {
            const configItems = getConfiguredItems(
                mentalHealthDetails,
                currentParticipant?.contractId
            );
            setArrayMentalHealth(configItems);
        }
    }, [mentalHealthDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (modStatusDetails?.length && currentParticipant?.contractId) {
            const configItems = getConfiguredItems(
                modStatusDetails,
                currentParticipant?.contractId
            );
            setArrayModStatus(configItems);
        }
    }, [modStatusDetails, currentParticipant?.contractId]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === 'Participant circumstance details have been updated') {
            setIsUpdateButtonDisabled(false);
            clearForm();
        }
    }, [successMessage]);

    // HELPER FUNCTIONS
    const clearForm = () => {
        setKeys(clearKeys(keys));
        setErrors({});
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const loadingError = () => {
        if (acceptanceToTakePartInServiceTypes?.length < 1)
            return ACCEPTANCE_TO_TAKE_PART_IN_SERVICE_ERROR;
        if (disabilityDetails?.length < 1) return DISABILITY_DETAILS_ERROR;
        if (hasDisabilityDetails?.length < 1) return HAS_DISABILITY_DETAILS_ERROR;
        if (mentalHealthDetails?.length < 1) return MENTAL_HEALTH_DETAILS_ERROR;
        if (modStatusDetails?.length < 1) return MOD_STATUS_DETAILS_ERROR;
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
    };

    const cleanUp = () => dispatch(clearCurrentParticipantCircumstances());

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSwitchAuditDisplayFields = (e) => {
        setIsAuditable(e.target.checked);
    };

    const updateAdditionalContacts = (additionalContactCards) => {
        setNewEntry((prev) => ({ ...prev, additionalContactCards }));
    };

    const onUpdateHasDisability = (id) => {
        const hasDisability = hasDisabilityDetails.find((el) => el.id === id)?.name;
        if (hasDisability?.toLowerCase() !== 'disabled') {
            setNewEntry((prev) => ({
                ...prev,
                hasDisabilityId: id,
                hasDisability,
                disabilityDetailIds: []
            }));
        } else setNewEntry((prev) => ({ ...prev, hasDisabilityId: id, hasDisability }));
    };

    const onUpdateHasMentalHealthProblems = (value) => {
        const key = 'hasMentalHealthProblems';
        if (value !== 'yes') {
            setNewEntry((prev) => ({
                ...prev,
                [key]: value,
                mentalHealthConditionIds: [],
                healthComment: ''
            }));
        } else setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSearchAuditItems = (field) => {
        if (field && newEntry?.id) {
            dispatch(loadParticipantAuditEvent('participant/circumstances', newEntry.id, field));
            setShowAuditModal(true);
            setAuditLabel(field);
        }
    };

    const onModalClose = (e, reason) => {
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        dispatch(clearParticipantAuditEventSearchData());
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = checkDiff(newEntry, initialStateOnEntry);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            ...newEntry,
            participantId: currentParticipant.id,
            additionalContactCards: newEntry.additionalContactCards.map((el) => ({
                ...el,
                id: null
            }))
        };
        dispatch(updateParticipantCircumstances(payload));
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    //  RENDER
    return (
        <div className={form.formWrapper}>
            <form onSubmit={onSubmit} data-testid="circumstances-form" className={form.form}>
                <div className={app.sectionToolBar}>
                    <h2 className={app.sectionHeading}>General Details</h2>
                    <Switch
                        id={'auditDisplayFields'}
                        label="Show Audit Fields"
                        disabled={!newEntry.id}
                        onOff={true}
                        customClass="auditSwitch"
                        checked={isAuditable}
                        onChange={onSwitchAuditDisplayFields}
                    />
                </div>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="hasDisabilityId"
                            key={keys.hasDisabilityId}
                            label="Disability"
                            placeholder="Select a has disability type..."
                            disabled={isDisabled}
                            menuItems={hasDisabilityDetails || []}
                            selectedId={newEntry.hasDisabilityId || ''}
                            selected={getObjFromId(hasDisabilityDetails, newEntry.hasDisabilityId)}
                            onChange={(chosenId) => onUpdateHasDisability(chosenId)}
                        />
                        {newEntry.hasDisability?.toLowerCase() === 'disabled' && (
                            <MultiSelect
                                id="disabilityDetailIds"
                                key={keys.disabilityDetailIds}
                                label="Disability - Info"
                                placeholder="Select disabilities..."
                                disabled={isDisabled}
                                menuItems={arrayDisabilities || []}
                                preSelectedIds={newEntry.disabilityDetailIds}
                                preSelects={arrayDisabilities?.filter((el) =>
                                    newEntry.disabilityDetailIds?.includes(el.id)
                                )}
                                onChange={(chosenIds) => onUpdate('disabilityDetailIds', chosenIds)}
                            />
                        )}
                        <RadioButtons
                            id="hasMentalHealthProblems"
                            label="Health/Mental Health Problems?"
                            disabled={isDisabled}
                            radioButtons={[
                                { id: 'no', label: 'No', onChangeVal: 'no' },
                                { id: 'yes', label: 'Yes', onChangeVal: 'yes' },
                                {
                                    id: 'preferNotToSay',
                                    label: 'Prefer not to say',
                                    onChangeVal: 'prefer not to say'
                                }
                            ]}
                            value={newEntry.hasMentalHealthProblems}
                            onChange={(option) => onUpdateHasMentalHealthProblems(option)}
                        />
                        {newEntry.hasMentalHealthProblems === 'yes' && (
                            <>
                                <MultiSelect
                                    id="mentalHealthConditionIds"
                                    key={keys.mentalHealthConditionIds}
                                    label="Health/Mental Health Problems - Info"
                                    placeholder="Select conditions..."
                                    disabled={isDisabled}
                                    menuItems={arrayMentalHealth || []}
                                    preSelectedIds={newEntry.mentalHealthConditionIds}
                                    preSelects={arrayMentalHealth?.filter((el) =>
                                        newEntry.mentalHealthConditionIds?.includes(el.id)
                                    )}
                                    onChange={(chosenIds) =>
                                        onUpdate('mentalHealthConditionIds', chosenIds)
                                    }
                                />
                                <TextInputField
                                    id="healthComment"
                                    label="Health Comment"
                                    placeholder="Enter health comment"
                                    disabled={isDisabled}
                                    value={newEntry.healthComment || ''}
                                    maxLength={_HEALTH_COMMENT_MAX_LENGTH}
                                    onChange={(e) => onUpdate('healthComment', e.target.value)}
                                />
                            </>
                        )}
                        <TextAreaField
                            id="reasonableAdjustments"
                            label="Reasonable Adjustments"
                            placeholder="Enter reasonable adjustments"
                            disabled={isDisabled}
                            rows={6}
                            value={newEntry.reasonableAdjustments || ''}
                            count={`${newEntry.reasonableAdjustments?.length || 0}/${_REASONABLE_ADJUSTMENTS_MAX_LENGTH}`}
                            maxLength={_REASONABLE_ADJUSTMENTS_MAX_LENGTH}
                            onChange={(e) => onUpdate('reasonableAdjustments', e.target.value)}
                        />
                        <SingleSelect
                            id={'acceptanceOfServiceId'}
                            key={keys.acceptanceOfServiceId}
                            label={'Acceptance to Take Part in the Service'}
                            placeholder="Select option..."
                            disabled={isDisabled}
                            menuItems={acceptanceToTakePartInServiceTypes}
                            selectedId={newEntry.acceptanceOfServiceId}
                            selected={getObjFromId(
                                acceptanceToTakePartInServiceTypes,
                                newEntry?.acceptanceOfServiceId
                            )}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            onChange={(chosenId) => onUpdate('acceptanceOfServiceId', chosenId)}
                        />
                        <RadioButtons
                            id="receiveServiceInEnglish"
                            label="Is the Service User Happy to Receive the Service in English?"
                            disabled={isDisabled}
                            value={newEntry.receiveServiceInEnglish ?? true}
                            onChange={(option) => onUpdate('receiveServiceInEnglish', option)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <RadioButtons
                            id="criminalRecord"
                            label="Criminal Record"
                            disabled={isDisabled}
                            radioButtons={[
                                { id: 'no', label: 'No', onChangeVal: 'no' },
                                { id: 'yes', label: 'Yes', onChangeVal: 'yes' },
                                { id: 'unknown', label: 'Unknown', onChangeVal: 'unknown' }
                            ]}
                            value={newEntry.hasCriminalRecord}
                            onChange={(option) => onUpdate('hasCriminalRecord', option)}
                        />
                        {newEntry.hasCriminalRecord === 'yes' && (
                            <RadioButtons
                                id="criminalConvictionSpent"
                                label="Criminal Conviction Spent?"
                                disabled={isDisabled}
                                value={newEntry.criminalConvictionSpent || false}
                                onChange={(value) => onUpdate('criminalConvictionSpent', value)}
                            />
                        )}
                        <RadioButtons
                            id="pv"
                            label="PV"
                            disabled={isDisabled}
                            value={newEntry.pv || false}
                            onChange={(option) => onUpdate('pv', option)}
                            tooltipMessage={newEntry.pv ? _TOOL_TIP_MESSAGE : ''}
                        />
                        <RadioButtons
                            id="trainingEmploymentRestriction"
                            label="Training and Employment Restrictions"
                            disabled={isDisabled}
                            value={newEntry.trainingEmploymentRestriction || false}
                            onChange={(option) => onUpdate('trainingEmploymentRestriction', option)}
                            tooltipMessage={
                                newEntry.trainingEmploymentRestriction ? _TOOL_TIP_MESSAGE : ''
                            }
                        />
                        <RadioButtons
                            id="ucb"
                            label="UCB"
                            disabled={isDisabled}
                            value={newEntry.ucb || false}
                            onChange={(option) => onUpdate('ucb', option)}
                            tooltipMessage={newEntry.ucb ? _TOOL_TIP_MESSAGE : ''}
                        />
                        <SingleSelect
                            id={'modStatusId'}
                            key={keys.modStatus}
                            label={'Are you part of the Armed Forces Community?'}
                            placeholder="Select status..."
                            disabled={isDisabled}
                            menuItems={arrayModStatus}
                            selectedId={newEntry.modStatusId}
                            selected={
                                arrayModStatus.find((el) => el.id === newEntry?.modStatusId) || {}
                            }
                            onChange={(chosenId) => onUpdate('modStatusId', chosenId)}
                        />
                    </div>
                </div>

                <AdditionalContacts
                    currentParticipant={currentParticipant}
                    additionalContacts={newEntry.additionalContactCards || []}
                    updateAdditionalContacts={updateAdditionalContacts}
                />

                {showAuditModal && (
                    <AuditCircumstancesModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        onClose={onModalClose}
                    />
                )}

                <Button
                    id="updateCircumstances"
                    content="Update Circumstances"
                    disabled={isDisabled || isUpdateButtonDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

export default Circumstances;
